import { PAYMENT_METHODS, paymentLabel, type PaymentMethod } from '../api/pos';
import { money } from '../format';

/**
 * How a bill is being paid: one tender, or split across several.
 *
 * In split mode every tender has its own amount box; a tender with an amount
 * is one the customer paid part of the bill with. The bill cannot be taken
 * until the amounts add up to the total exactly.
 */
export interface TenderValue {
  split: boolean;
  /** The tender when not splitting. */
  method: PaymentMethod;
  /** What was typed per tender while splitting. */
  amounts: Record<PaymentMethod, string>;
}

const NO_AMOUNTS: Record<PaymentMethod, string> = { CASH: '', CARD: '', UPI: '' };

export const EMPTY_TENDER: TenderValue = { split: false, method: 'CASH', amounts: NO_AMOUNTS };

const paise = (rupees: number) => Math.round(rupees * 100);
const amountOf = (raw: string) => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? paise(n) : 0;
};

/** The tenders with an amount against them, in rupees. */
export function tenderShares(
  value: TenderValue,
): Array<{ method: PaymentMethod; amount: number }> {
  if (!value.split) return [];
  return PAYMENT_METHODS.flatMap((method) => {
    const p = amountOf(value.amounts[method]);
    return p > 0 ? [{ method, amount: p / 100 }] : [];
  });
}

/** Why the bill cannot be taken yet, or null when the tender is fit to send. */
export function tenderProblem(value: TenderValue, total: number): string | null {
  if (!value.split) return null;
  const entered = tenderShares(value).reduce((sum, s) => sum + paise(s.amount), 0);
  const due = paise(total);
  if (entered === 0) return 'Enter how much was paid by each tender.';
  if (entered === due) return null;
  return entered < due
    ? `The amounts come to ${money(entered / 100)} — ${money((due - entered) / 100)} short of the bill.`
    : `The amounts come to ${money(entered / 100)} — ${money((entered - due) / 100)} more than the bill.`;
}

/** The fields to put on the request. */
export function tenderPayload(
  value: TenderValue,
):
  | { paymentMethod: PaymentMethod }
  | { payments: Array<{ method: PaymentMethod; amount: number }> } {
  const shares = tenderShares(value);
  if (!value.split) return { paymentMethod: value.method };
  if (shares.length === 1) return { paymentMethod: shares[0].method };
  return { payments: shares };
}

/** "Cash ₹500.00 + UPI ₹1,000.00", or just "Cash" for one tender. */
export function tenderLabel(value: TenderValue): string {
  const shares = tenderShares(value);
  if (!value.split || shares.length <= 1) {
    return paymentLabel(shares[0]?.method ?? value.method);
  }
  return shares.map((s) => `${paymentLabel(s.method)} ${money(s.amount)}`).join(' + ');
}

/** A tender state that reads back what an existing bill was paid with. */
export function tenderFromSale(
  splits: Array<{ method: PaymentMethod; amount: number | string }> | null | undefined,
  method: PaymentMethod | undefined,
): TenderValue {
  if (splits && splits.length > 1) {
    const amounts = { ...NO_AMOUNTS };
    for (const s of splits) amounts[s.method] = String(Number(s.amount));
    return { split: true, method: method ?? 'CASH', amounts };
  }
  return { ...EMPTY_TENDER, method: method ?? 'CASH' };
}

export default function TenderPicker({
  value,
  total,
  onChange,
  showProblem,
  idPrefix,
}: {
  value: TenderValue;
  /** What the bill comes to, after any discount. */
  total: number;
  onChange: (next: TenderValue) => void;
  /** Show the mismatch even before the amounts are touched. */
  showProblem?: boolean;
  idPrefix: string;
}) {
  const problem = tenderProblem(value, total);
  const entered = tenderShares(value).reduce((sum, s) => sum + paise(s.amount), 0);
  const left = (paise(total) - entered) / 100;

  function setAmount(method: PaymentMethod, raw: string) {
    const cleaned = raw
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/^(\d*\.\d{0,2}).*$/, '$1');
    onChange({ ...value, amounts: { ...value.amounts, [method]: cleaned } });
  }

  /** Put whatever is still unpaid into this tender. */
  function takeRest(method: PaymentMethod) {
    const others = PAYMENT_METHODS.filter((m) => m !== method).reduce(
      (sum, m) => sum + amountOf(value.amounts[m]),
      0,
    );
    const rest = (paise(total) - others) / 100;
    setAmount(method, rest > 0 ? String(rest) : '');
  }

  function setSplit(split: boolean) {
    if (split) {
      // Start from the whole bill on the tender that was chosen, so splitting
      // is "move some of it elsewhere" rather than typing from nothing.
      onChange({
        ...value,
        split: true,
        amounts: { ...NO_AMOUNTS, [value.method]: total > 0 ? String(total) : '' },
      });
    } else {
      onChange({ ...value, split: false, amounts: NO_AMOUNTS });
    }
  }

  return (
    <div className="tender">
      <span className="field-label">Payment received by</span>

      {!value.split ? (
        <div className="tender-row">
          {PAYMENT_METHODS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === value.method ? 'tender-btn tender-active' : 'tender-btn'}
              onClick={() => onChange({ ...value, method: m })}
            >
              {paymentLabel(m)}
            </button>
          ))}
        </div>
      ) : (
        <div className="tender-split">
          {PAYMENT_METHODS.map((m) => (
            <div key={m} className="tender-split-row">
              <label htmlFor={`${idPrefix}-${m}`}>{paymentLabel(m)}</label>
              <span className="discount-rupee" aria-hidden="true">
                ₹
              </span>
              <input
                id={`${idPrefix}-${m}`}
                className="input"
                inputMode="decimal"
                value={value.amounts[m]}
                onChange={(e) => setAmount(m, e.target.value)}
                placeholder="0"
                autoComplete="off"
              />
              <button type="button" className="link-btn" onClick={() => takeRest(m)}>
                Rest
              </button>
            </div>
          ))}
          <p
            className={problem && (showProblem || entered > 0) ? 'error small' : 'muted small'}
            role="status"
          >
            {problem && (showProblem || entered > 0)
              ? problem
              : left === 0
                ? `Adds up to the bill: ${money(total)}.`
                : `Bill ${money(total)}.`}
          </p>
        </div>
      )}

      <button type="button" className="link-btn" onClick={() => setSplit(!value.split)}>
        {value.split ? 'Pay with one tender instead' : 'Split between cash, UPI and card'}
      </button>
    </div>
  );
}
